import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    Typography,
} from '@mui/material';
import {
    ActivityEntityName,
    useGetEntityActivitiesQuery,
} from '../../../services';
import {
    formatBusinessDateTime,
    getActivityTypeLabel,
    isSnapshotCaptureFailed,
    SNAPSHOT_CAPTURE_FAILED_HINT,
} from '../../../helpers';
import { ActivityChangesContent } from '../dialogs';
import { ArrowDropDownIcon, DRouterLink } from '../../dum';
import { WarningAmber } from '@mui/icons-material';

const ENTITY_HISTORY_LIMIT = 50;

interface IEntityHistorialProps {
    entityName: ActivityEntityName;
    entityId?: number | null;
    /**
     * Shown before the record exists. Passed in rather than derived from
     * entityName because Spanish gender and noun differ per entity ("la venta
     * ha sido guardada" vs "la compra ha sido guardada"), and a generic string
     * would read worse than the caller's own copy.
     */
    unsavedMessage: string;
    emptyMessage: string;
}

/**
 * Audit history for one record: every activity logged against it, newest first,
 * each expandable into its before/after diff.
 *
 * Entity-agnostic on purpose — getEntityActivities takes the ActivityEntityName
 * enum and the differ flattens whatever shape the snapshot has, so wiring a new
 * entity is a call site, not a component.
 *
 * Rendered for the three global roles — Super, General and Asistente General —
 * and the tab is omitted for everyone else in the form. That is a convenience,
 * not the control: getEntityActivities and getActivity are both gated with
 * RoleId.ADMIN on the server, which GqlRolesGuard admits those same three roles
 * to on a query, so hiding the tab only avoids a dead click. See
 * use-historial-tab.tsx for the one place that check lives.
 *
 * Each row's diff loads lazily on expand rather than shipping up to 50 full
 * snapshots with the list.
 */
export function EntityHistorial({
    entityName,
    entityId,
    unsavedMessage,
    emptyMessage,
}: IEntityHistorialProps) {
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const { data, loading } = useGetEntityActivitiesQuery({
        variables: {
            EntityName: entityName,
            EntityId: entityId!,
        },
        skip: !entityId,
        fetchPolicy: 'cache-and-network',
    });

    if (!entityId) {
        return (
            <Typography color={'text.secondary'} variant={'body2'}>
                {unsavedMessage}
            </Typography>
        );
    }

    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const activities = data?.getEntityActivities ?? [];

    if (activities.length === 0) {
        return (
            <Typography color={'text.secondary'} variant={'body2'}>
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <Box>
            {activities.length >= ENTITY_HISTORY_LIMIT ? (
                <Typography
                    color={'text.secondary'}
                    variant={'body2'}
                    sx={{ mb: 1 }}
                >
                    Mostrando las {ENTITY_HISTORY_LIMIT} actividades más
                    recientes.{' '}
                    <DRouterLink to={'/activities'}>
                        Consulta Actividades
                    </DRouterLink>{' '}
                    para ver actividades anteriores.
                </Typography>
            ) : null}
            {activities.map((activity) => {
                const isExpanded = expandedId === activity.id;
                return (
                    <Accordion
                        key={activity.id}
                        expanded={isExpanded}
                        onChange={() => {
                            setExpandedId(isExpanded ? null : activity.id);
                        }}
                        disableGutters
                    >
                        <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    alignItems: 'baseline',
                                }}
                            >
                                <Typography variant={'body2'}>
                                    {getActivityTypeLabel(activity.type)}
                                </Typography>
                                {/*
                                  Visible BEFORE expanding: otherwise the only
                                  way to find out the history was not captured is
                                  to open the row and read an empty diff.
                                */}
                                {isSnapshotCaptureFailed(
                                    activity.snapshot_status,
                                ) ? (
                                    <WarningAmber
                                        fontSize={'small'}
                                        color={'warning'}
                                        titleAccess={
                                            SNAPSHOT_CAPTURE_FAILED_HINT
                                        }
                                    />
                                ) : null}
                                <Typography
                                    variant={'caption'}
                                    color={'text.secondary'}
                                >
                                    {activity.user?.fullname || '—'}
                                    {/*
                                      created_at is a true UTC instant, shown on
                                      the Mexico City business day — the same
                                      reading the activities date filter uses,
                                      and independent of the viewer's machine
                                      clock.
                                    */}
                                    {activity.created_at
                                        ? ` · ${formatBusinessDateTime(
                                              activity.created_at,
                                          )}`
                                        : ''}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {/*
                              Mounted only while expanded, so the query fires on
                              demand and collapsing does not keep polling.
                            */}
                            {isExpanded ? (
                                <ActivityChangesContent
                                    activityId={activity.id}
                                    showHeader={false}
                                />
                            ) : null}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}
